import React from 'react'
import { PersonAffiliation } from '../../../types'
import { formatPersonRoleLabel } from '../../../utils/format'

/**
 * Horizontal pill-bar showing the distribution of active role types.
 *
 * Example:
 *   Direktør ████████ 4   Bestyrelsesmedlem ████ 2   Ejer ██ 1
 */
interface PersonRoleBreakdownProps {
  affiliations: PersonAffiliation[]
  /** Tighter layout for the overview strip. */
  compact?: boolean
}

function countActiveRoles(affiliations: PersonAffiliation[]): Map<string, number> {
  const counts = new Map<string, number>()
  for (const aff of affiliations) {
    for (const role of aff.roles) {
      if (!role.isActive) continue
      const key = formatPersonRoleLabel(role.function || role.organizationType)
      counts.set(key, (counts.get(key) ?? 0) + 1)
    }
  }
  return counts
}

const PersonRoleBreakdown: React.FC<PersonRoleBreakdownProps> = ({
  affiliations,
  compact = false,
}) => {
  const counts = countActiveRoles(affiliations)
  const totalActive = Array.from(counts.values()).reduce((n, c) => n + c, 0)
  const title = `Aktive roller (${totalActive})`

  if (counts.size === 0) {
    return compact ? (
      <div className="space-y-2">
        <p className="text-[13px] font-semibold text-ink-500">
          {title}
        </p>
        <p className="text-xs text-ink-500">Ingen aktive roller.</p>
      </div>
    ) : null
  }

  const sorted = Array.from(counts.entries()).sort((a, b) => b[1] - a[1])
  const max = sorted[0][1]

  return (
    <div className={`${compact ? '' : 'mt-4'} space-y-2`}>
      <p className="text-[13px] font-semibold text-ink-500">
        {title}
      </p>
      <ul className={compact ? 'space-y-1.5' : 'space-y-2'} aria-label="Aktive roller">
        {sorted.map(([label, count]) => (
          <li key={label} className="flex items-center gap-2 min-w-0">
            <span
              className={`${compact ? 'text-xs w-32' : 'text-sm w-40'} text-ink-700 shrink-0 truncate`}
              title={label}
            >
              {label}
            </span>
            <div
              className={`flex-1 bg-paper-200 rounded-full overflow-hidden ${compact ? 'h-1' : 'h-1.5'}`}
            >
              <div
                className="h-full bg-ink-500 rounded-full"
                style={{ width: `${(count / max) * 100}%` }}
                aria-hidden
              />
            </div>
            <span className="tabular-nums text-xs text-ink shrink-0 w-4 text-right">
              {count}
            </span>
          </li>
        ))}
      </ul>
    </div>
  )
}

export default PersonRoleBreakdown
